import {
    AddBankAccountRequest,
    AddBankAccountResponse,
    AddBankAccountRequestType,
    BankAccountRequest,
    CreateDocumentUploadUrlRequest,
    CreateDocumentUploadUrlResponse,
    DematAccountRequest,
    GetDematApplicationStatusResponse,
    KillSwitchStatusResponse,
    ListPendingBankVerificationsResponse,
    SetKillSwitchRequest,
    SetPrimaryBankAccountRequest,
    SubmitWorkflowStepRequest,
    TradingAccount,
    TradingAccountProfile,
    UpsertNomineesRequest
} from '../../proto/generated/trading-bridge'
import api from './axiosClient'
import {
    NomineeChangeRequest
} from '../../pages/settings/tradingAccountProfile/nomineeChangeRequest.types'
import {
    normalizeNomineeChangeRequest
} from '../../pages/settings/tradingAccountProfile/nomineeChangeRequest.utils'

const protobufHeaders = {
    Accept: 'application/x-protobuf'
}

const decodeTradingAccount = (data: ArrayBuffer): TradingAccount =>
    TradingAccount.decode(new Uint8Array(data))

const decodeTradingAccountProfile = (data: ArrayBuffer): TradingAccountProfile =>
    TradingAccountProfile.decode(new Uint8Array(data))

export const tradingAccountFromProfile = (profile: TradingAccountProfile): TradingAccount | undefined =>
    profile.tradingAccount?.accountId ? profile.tradingAccount : undefined

export const getAccountProfile = async (): Promise<TradingAccountProfile> => {
    const response = await api.get('/trading-accounts/profile', {
        headers: {
            ...protobufHeaders,
            'X-Suppress-Error': 'true'
        },
        responseType: 'arraybuffer'
    })
    return decodeTradingAccountProfile(response.data)
}

export const addBankAccount = async (request: BankAccountRequest): Promise<TradingAccount> => {
    const response = await api.post('/trading-accounts/bank-accounts', JSON.stringify(BankAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const updateBankAccount = async (request: BankAccountRequest): Promise<TradingAccount> => {
    const response = await api.put('/trading-accounts/bank-accounts', JSON.stringify(BankAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const removeBankAccount = async (id: number): Promise<TradingAccount> => {
    const response = await api.delete(`/trading-accounts/bank-accounts/${id}`, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const setPrimaryBankAccount = async (id: number): Promise<TradingAccount> => {
    const body = SetPrimaryBankAccountRequest.create({ id })
    const response = await api.post(`/trading-accounts/bank-accounts/${id}/make-primary`, JSON.stringify(SetPrimaryBankAccountRequest.toJSON(body)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

/**
 * Initiate a UPI-based bank account addition.
 * Returns an AddBankAccountResponse containing upiIntent with the
 * transactionId and upiIntentString to open on the device.
 */
export const addBankAccountViaUpi = async (setAsPrimary: boolean): Promise<AddBankAccountResponse> => {
    const body = AddBankAccountRequest.create({
        setAsPrimary,
        requestType: AddBankAccountRequestType.UPI,
    })
    const response = await api.post(
        '/trading-accounts/bank-accounts',
        JSON.stringify(AddBankAccountRequest.toJSON(body)),
        { headers: protobufHeaders, responseType: 'arraybuffer' }
    )
    return AddBankAccountResponse.decode(new Uint8Array(response.data))
}

/**
 * Submit a MANUAL bank account addition (IFSC + account number).
 * Creates a pending change request for review.
 */
export const addBankAccountManual = async (params: {
    setAsPrimary: boolean
    ifsc: string
    accountNumber: string
}): Promise<AddBankAccountResponse> => {
    const body = AddBankAccountRequest.create({
        setAsPrimary: params.setAsPrimary,
        requestType: AddBankAccountRequestType.MANUAL,
        ifsc: params.ifsc,
        accountNumber: params.accountNumber,
    })
    const response = await api.post(
        '/trading-accounts/bank-accounts',
        JSON.stringify(AddBankAccountRequest.toJSON(body)),
        { headers: protobufHeaders, responseType: 'arraybuffer' }
    )
    return AddBankAccountResponse.decode(new Uint8Array(response.data))
}

/**
 * Fetch any pending UPI bank-account verification transactions.
 * Call this on mount to restore an in-progress verification after the user
 * navigates away and comes back.
 */
export const getPendingBankVerifications = async (): Promise<ListPendingBankVerificationsResponse> => {
    const response = await api.get('/trading-accounts/bank-accounts/verifications/pending', {
        headers: {
            ...protobufHeaders,
            'X-Suppress-Error': 'true',
        },
        responseType: 'arraybuffer',
    })
    return ListPendingBankVerificationsResponse.decode(new Uint8Array(response.data))
}

export const upsertNominees = async (request: UpsertNomineesRequest): Promise<TradingAccount> => {
    const response = await api.put('/trading-accounts/nominees', JSON.stringify(UpsertNomineesRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const getNomineeChangeRequest = async (): Promise<NomineeChangeRequest | null> => {
    try {
        const response = await api.get('/trading-accounts/nominees/change-request', {
            headers: {
                Accept: 'application/json',
                'X-Suppress-Error': 'true'
            },
            responseType: 'json'
        })
        return normalizeNomineeChangeRequest(response.data)
    } catch {
        return null
    }
}

export const submitNomineeChangeRequest = async (request: UpsertNomineesRequest): Promise<NomineeChangeRequest> => {
    const response = await api.put(
        '/trading-accounts/nominees/change-request',
        UpsertNomineesRequest.toJSON(request),
        {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            responseType: 'json'
        }
    )
    const normalized = normalizeNomineeChangeRequest(response.data)
    if (!normalized) {
        throw new Error('Invalid nominee change request response')
    }
    return normalized
}

export const addDematAccount = async (request: DematAccountRequest): Promise<TradingAccount> => {
    const response = await api.post('/trading-accounts/demat-accounts', JSON.stringify(DematAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const updateDematAccount = async (request: DematAccountRequest): Promise<TradingAccount> => {
    const response = await api.put('/trading-accounts/demat-accounts', JSON.stringify(DematAccountRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const removeDematAccount = async (id: number): Promise<TradingAccount> => {
    const response = await api.delete(`/trading-accounts/demat-accounts/${id}`, {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const createDocumentUploadUrl = async (request: CreateDocumentUploadUrlRequest): Promise<CreateDocumentUploadUrlResponse> => {
    const response = await api.post('/trading-accounts/documents/upload-url', JSON.stringify(CreateDocumentUploadUrlRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return CreateDocumentUploadUrlResponse.decode(new Uint8Array(response.data))
}

export const setKillSwitch = async (request: SetKillSwitchRequest): Promise<TradingAccount> => {
    const response = await api.post('/trading-accounts/kill-switch', JSON.stringify(SetKillSwitchRequest.toJSON(request)), {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return decodeTradingAccount(response.data)
}

export const getKillSwitchStatus = async (): Promise<KillSwitchStatusResponse> => {
    const response = await api.get('/trading-accounts/kill-switch', {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return KillSwitchStatusResponse.decode(new Uint8Array(response.data))
}

export const initiateDematApplication = async (): Promise<TradingAccountProfile> => {
    const response = await api.post('/trading-accounts/demat/apply', null, {
        headers: {
            ...protobufHeaders,
            'X-Suppress-Error': 'true'
        },
        responseType: 'arraybuffer'
    })
    return decodeTradingAccountProfile(response.data)
}

export const getDematApplicationStatus = async (): Promise<GetDematApplicationStatusResponse> => {
    const response = await api.get('/trading-accounts/demat/status', {
        headers: protobufHeaders,
        responseType: 'arraybuffer'
    })
    return GetDematApplicationStatusResponse.decode(new Uint8Array(response.data))
}

export const resumeWorkflow = async (workflowId: string): Promise<TradingAccountProfile> => {
    const response = await api.post(
        '/trading-accounts/demat/resume',
        { workflowId },
        {
            headers: {
                ...protobufHeaders,
                'X-Suppress-Error': 'true'
            },
            responseType: 'arraybuffer'
        }
    )
    return decodeTradingAccountProfile(response.data)
}

export const submitWorkflowStep = async (
    workflowId: string,
    stepId: string,
    data: Record<string, string>
): Promise<TradingAccountProfile> => {
    const body = SubmitWorkflowStepRequest.create({
        workflowId,
        stepId,
        data
    })
    const response = await api.post(
        '/trading-accounts/workflows/steps',
        JSON.stringify(SubmitWorkflowStepRequest.toJSON(body)),
        {
            headers: {
                ...protobufHeaders,
                'X-Suppress-Error': 'true'
            },
            responseType: 'arraybuffer'
        }
    )
    return decodeTradingAccountProfile(response.data)
}
